import { useState, useEffect } from 'react';

/**
 * Kawaii character voice presets for Web Speech API.
 * Each preset modifies pitch, rate, and voice selection to give a distinct personality.
 */
export const VOICE_PRESETS = {
  default: {
    id: 'default',
    label: 'Vanlig röst',
    emoji: '🗣️',
    pitch: 1.0,
    rate: 0.95,
    voiceHint: 'sv',
    suffix: '',
  },
  cat: {
    id: 'cat',
    label: 'Kisse-lärare',
    emoji: '🐱',
    pitch: 1.35,
    rate: 0.9,
    voiceHint: 'sv-female',
    suffix: ' Mjau.',
  },
  bear: {
    id: 'bear',
    label: 'Gos-björn',
    emoji: '🐻',
    pitch: 0.6,
    rate: 0.78,
    voiceHint: 'sv-male',
    suffix: '',
  },
  bunny: {
    id: 'bunny',
    label: 'Energi-kanin',
    emoji: '🐇',
    pitch: 1.5,
    rate: 1.25,
    voiceHint: 'sv-female',
    suffix: ' Yay!',
  },
  owl: {
    id: 'owl',
    label: 'Visa ugglan',
    emoji: '🦉',
    pitch: 0.85,
    rate: 0.82,
    voiceHint: 'sv',
    suffix: '',
  },
  dragon: {
    id: 'dragon',
    label: 'Drake',
    emoji: '🐉',
    pitch: 0.5,
    rate: 0.85,
    voiceHint: 'sv-male',
    suffix: '',
  },
  fairy: {
    id: 'fairy',
    label: 'Fe-röst',
    emoji: '🧚',
    pitch: 1.8,
    rate: 1.1,
    voiceHint: 'sv-female',
    suffix: ' ✨',
  },
};

const STORAGE_KEY = 'focus_critter_voice_preset';

/**
 * Hook to read/update the user's chosen voice preset (persists in localStorage).
 */
export function useVoicePreset() {
  const [presetId, setPresetId] = useState(() => {
    if (typeof window === 'undefined') return 'default';
    return localStorage.getItem(STORAGE_KEY) || 'default';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, presetId);
    }
  }, [presetId]);

  return [VOICE_PRESETS[presetId] || VOICE_PRESETS.default, setPresetId];
}

/**
 * Pick a browser voice that best matches the hint.
 */
export function pickVoice(voices, hint = 'sv') {
  if (!voices || !voices.length) return null;
  const wantsFemale = hint.includes('female');
  const wantsMale = hint.includes('male');
  const sv = voices.filter((v) => v.lang?.toLowerCase().startsWith('sv'));
  const pool = sv.length ? sv : voices;
  if (wantsFemale) {
    const f = pool.find((v) => /female|flicka|tjej|anna|alva|elin|klara|lisa|maja/i.test(v.name));
    if (f) return f;
  }
  if (wantsMale) {
    const m = pool.find((v) => /male|man|mans|carl|erik|oskar|johan|gustav/i.test(v.name));
    if (m) return m;
  }
  return pool[0];
}

/**
 * Speak text using a given preset. Returns a cleanup function.
 */
export function speakWithPreset(text, preset = VOICE_PRESETS.default, onEnd) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return () => {};
  const synth = window.speechSynthesis;
  if (synth.speaking) synth.cancel();
  const finalText = (text || '').trim() + (preset.suffix || '');
  if (!finalText) return () => {};
  const u = new SpeechSynthesisUtterance(finalText);
  u.pitch = preset.pitch;
  u.rate = preset.rate;
  const voice = pickVoice(synth.getVoices(), preset.voiceHint);
  if (voice) u.voice = voice;
  u.lang = voice?.lang || 'sv-SE';
  if (onEnd) u.onend = onEnd;
  synth.speak(u);
  return () => { try { synth.cancel(); } catch { /* ignore */ } };
}
